import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker, useLocation } from "react-router-dom";
import { CensusChangesWarningDialog } from "../components/DraftMode/CensusChangesWarningDialog";
import { NavigationWarningDialog } from "../components/DraftMode/NavigationWarningDialog";
import { performGlobalRefreshCore } from "../components/header/hooks";
import { scheduleWeekQuery } from "../hooks/scheduleCache";
import { patchScheduleDay, resetScheduleAfterDraft } from "../hooks/scheduleCacheWrites";
import { useErrorSnackbar } from "../hooks/useErrorSnackbar";
import { toChangeSlot } from "../hooks/useHandleNurseChange";
import { useInvalidateCallIns } from "../hooks/useInvalidateCallIns";
import { useLastNameFirst } from "../hooks/useNameFormat";
import { useFeatureFlag } from "../hooks/useOpenFeatureFlag";
import { usePublishChanges } from "../hooks/usePublishChanges";
import { useRosterLookup } from "../hooks/useRosterLookup";
import { useScheduleSaves } from "../hooks/useScheduleSaves";
import { useScheduleCacheEnabled, useScheduleStartDay } from "../hooks/useScheduleWeekRules";
import { backendService } from "../services/backend.service";
import { localStorageService } from "../services/LocalStorageService";
import { deepCloneObject } from "../utils/dateUtils";
import { type DraftDay, draftCensusChangeOf, draftDayOf, draftShiftChangesOf } from "../utils/draftDay";
import {
	ChangesSource,
	ChangeType,
	type DraftChange,
	DraftChangeType,
	type HospitalData,
	type NurseInShift,
	type PublishRequestBody,
	type ScheduleDay,
	type ShiftChange,
} from "../utils/models";
import { setDraftModeActive } from "../utils/networkWrapper";
import { editCachedDay, panelWeeksOf } from "../utils/panelWeeks";
import { toIsoDate, weekStartsBetween } from "../utils/scheduleWeeks";
import { useAuth } from "./AuthProvider";
import { useAnalytics } from "./analytics/hooks";
import { useChosenDepartment } from "./ChosenDepartment";
import { DraftModeContext } from "./DraftModeContext";
import { FEATURE_FLAGS } from "./FeatureFlagsProvider";
import { type ScheduleEdit, useHospitalData } from "./HospitalDataProvider";
import { useNotificationsContext } from "./NotificationsContext";
import { useRequestsContext } from "./RequestsContext";
import { useSnackbar } from "./SnackbarContext";

const DAY_SCHEDULE_PATH = /day-schedule\/(\d{4}-\d{2}-\d{2})/;
// DEFAULT: the drafted week's query key off the Day view, where the query is disabled and asks for nothing.
const NO_WEEK_START = "";

export const DraftModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const location = useLocation();
	// A draft with the cache off copies and restores the old list; the edits reach the cache through the provider's writer.
	const { hospitalData, setHospitalData, setDraftDayWriter, refreshDepartmentWageData, refreshNursesData } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const { afterScheduleWrite, oldListSlotOf } = useScheduleSaves();
	const showErrorSnackbar = useErrorSnackbar();
	const isCacheEnabled = useScheduleCacheEnabled();
	const startDay = useScheduleStartDay();
	const lastNameFirst = useLastNameFirst();
	const { refreshRequests } = useRequestsContext();
	const { fetchNotifications } = useNotificationsContext();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { mutateAsync: publishChangesAsync, isPending: isPublishing } = usePublishChanges();
	const invalidateCallIns = useInvalidateCallIns();
	const queryClient = useQueryClient();
	const refreshWageBreakdown = useCallback(async () => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ["shift-wage-breakdown"] }),
			queryClient.invalidateQueries({ queryKey: ["pto-costs"] }),
			queryClient.invalidateQueries({ queryKey: ["nurse-overtime"] }),
			refreshDepartmentWageData(),
		]);
	}, [queryClient, refreshDepartmentWageData]);
	const {
		userInfo: { id: userId },
	} = useAuth();

	const [isDraftMode, setIsDraftMode] = useState<boolean>(false);
	const [draftChanges, setDraftChanges] = useState<DraftChange[]>([]);
	const [originalData, setOriginalData] = useState<HospitalData>();
	const [originalDay, setOriginalDay] = useState<DraftDay>();
	// The other days a draft's edits reach on the cache (a move's other end): each one's copy from before its first edit, and now.
	const [touchedDays, setTouchedDays] = useState<Record<string, { original: DraftDay; active: DraftDay }>>({});
	const [locallyApprovedRequests, setLocallyApprovedRequests] = useState<string[]>([]);
	const [showCensusWarning, setShowCensusWarning] = useState<boolean>(false);
	const [pendingPublishData, setPendingPublishData] = useState<{
		censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }>;
		censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }>;
		newCensus: number | undefined;
	} | null>(null);

	const isDraftModeEnabled = useFeatureFlag(FEATURE_FLAGS.DraftModeEnabled);

	// The Day view's day: the one day a draft edits, since leaving it asks to publish or discard first.
	const dayViewDate = useMemo(() => {
		const match = location.pathname.match(DAY_SCHEDULE_PATH);
		return match ? DateTime.fromISO(match[1]) : undefined;
	}, [location.pathname]);
	const isDraftOnCache = isDraftMode && isCacheEnabled;
	const dayViewWeek = useQuery({
		...scheduleWeekQuery(chosenDepartment.department_id, dayViewDate ? weekStartsBetween(dayViewDate, dayViewDate, startDay)[0] : NO_WEEK_START),
		enabled: isDraftModeEnabled && isCacheEnabled && dayViewDate !== undefined,
		// The drafted edits live in this week until Publish or Discard reloads it, so it never refetches on its own.
		...(isDraftOnCache && { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY }),
	});

	const activeDayData = useMemo<DraftDay | undefined>(() => {
		if (!isDraftModeEnabled || !dayViewDate) return;
		if (!isCacheEnabled) return draftDayOf(hospitalData.weeks, dayViewDate);
		const cachedDay = dayViewWeek.data?.days.find((day) => toIsoDate(day.date) === toIsoDate(dayViewDate));
		return cachedDay && draftDayOf(panelWeeksOf([cachedDay], getNurseById, lastNameFirst), dayViewDate);
	}, [isDraftModeEnabled, dayViewDate, isCacheEnabled, hospitalData.weeks, dayViewWeek.data, getNurseById, lastNameFirst]);

	// While a draft is open on the cache, every edit of the old list's day is made to that cached day too.
	const editDraftedCachedDay = useCallback(
		(date: DateTime, edit: ScheduleEdit) => {
			const isoDate = toIsoDate(date);
			const draftDayOfCached = (day: ScheduleDay) => draftDayOf(panelWeeksOf([day], getNurseById, lastNameFirst), date);
			patchScheduleDay(queryClient, chosenDepartment.department_id, date, (day) => {
				// The provider's edits read only the weeks, so the rest of the old list rides along unchanged.
				const editedDay = editCachedDay(day, (weeks) => edit({ ...hospitalData, weeks }).weeks, getNurseById, lastNameFirst);
				const active = isoDate === (originalDay && toIsoDate(originalDay.date)) ? undefined : draftDayOfCached(editedDay);
				if (active) {
					setTouchedDays((days) => {
						const original = days[isoDate]?.original ?? draftDayOfCached(day);
						return original ? { ...days, [isoDate]: { original, active } } : days;
					});
				}
				return editedDay;
			}).catch((error: unknown) => showErrorSnackbar(t("draft.mode.edit.failed"), { date: isoDate }, error));
		},
		[queryClient, chosenDepartment.department_id, hospitalData, originalDay, getNurseById, lastNameFirst, showErrorSnackbar, t],
	);
	useEffect(() => {
		if (!isDraftOnCache) return;
		setDraftDayWriter(editDraftedCachedDay);
		return () => setDraftDayWriter(null);
	}, [isDraftOnCache, editDraftedCachedDay, setDraftDayWriter]);

	const hasUnsavedChanges = useMemo<boolean>(
		() => !!draftChanges.length || !!locallyApprovedRequests.length,
		[draftChanges.length, locallyApprovedRequests.length],
	);

	const blocker = useBlocker(({ currentLocation, nextLocation }) => isDraftMode && currentLocation.pathname !== nextLocation.pathname);

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => isDraftMode && e.preventDefault();
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isDraftMode]);

	const calculateDraftChanges = useCallback((): DraftChange[] => {
		if (!isDraftModeEnabled || !isDraftMode || !originalDay || !activeDayData) return [];
		const censusChange = draftCensusChangeOf(originalDay, activeDayData);
		return [
			...draftShiftChangesOf(originalDay, activeDayData),
			...Object.values(touchedDays).flatMap(({ original, active }) => draftShiftChangesOf(original, active)),
			...(censusChange ? [censusChange] : []),
		];
	}, [isDraftModeEnabled, isDraftMode, originalDay, activeDayData, touchedDays]);

	useEffect(() => {
		if (isDraftMode) setDraftChanges(calculateDraftChanges());
	}, [isDraftMode, calculateDraftChanges]);

	const enableDraftMode = useCallback((): void => {
		if (!activeDayData) throw new Error("Draft mode was asked for before the Day view's day loaded");
		track("draft_mode_enabled");
		showSnackbar(t("draft.mode.enabled"), "info");
		setOriginalData(deepCloneObject(hospitalData));
		// With the cache off the day's shifts are the old list's own objects, which the edits change in place.
		setOriginalDay({ ...activeDayData, shifts: deepCloneObject(activeDayData.shifts) });
		setTouchedDays({});
		setIsDraftMode(true);
		setDraftChanges([]);
		setDraftModeActive(true);
	}, [hospitalData, activeDayData, showSnackbar, t, track]);

	const disableDraftMode = useCallback((): void => {
		track("draft_mode_disabled");
		showSnackbar(t("draft.mode.disabled"), "info");
		setIsDraftMode(false);
		setDraftChanges([]);
		setOriginalData(undefined);
		setOriginalDay(undefined);
		setTouchedDays({});
		setLocallyApprovedRequests([]);
		setDraftModeActive(false);

		localStorageService.resetClientGroupedRefreshData(userId);
	}, [showSnackbar, t, track, userId]);

	const discardChanges = useCallback((): void => {
		track("draft_changes_discarded", { change_count: draftChanges.length, approved_requests: locallyApprovedRequests.length });
		showSnackbar(t("draft.mode.changes.discarded", { count: draftChanges.length + locallyApprovedRequests.length }), "info");
		setDraftChanges([]);
		setLocallyApprovedRequests([]);
		setIsDraftMode(false);
		setDraftModeActive(false);
		if (originalData) setHospitalData(originalData);
		// Drafts never reach the server, so reloading the cached weeks discards them there.
		void resetScheduleAfterDraft(queryClient, chosenDepartment.department_id);
		setOriginalData(undefined);
		setOriginalDay(undefined);
		setTouchedDays({});
	}, [originalData, draftChanges.length, locallyApprovedRequests.length, setHospitalData, showSnackbar, t, track, queryClient, chosenDepartment.department_id]);

	const executePublish = useCallback(
		async (
			censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }>,
			censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }>,
		): Promise<void> => {
			try {
				if (!originalDay) throw new Error("Publishing a draft that has no drafted day");
				const draftDates = new Map([originalDay, ...Object.values(touchedDays).map(({ original }) => original)].map((day) => [toIsoDate(day.date), day.date]));
				const dateOfChange = (draftChange: DraftChange): DateTime => {
					const changeDate = draftChange.payload.date && draftDates.get(draftChange.payload.date);
					if (!changeDate) throw new Error(`Draft change ${draftChange.id} is on no drafted day`);
					return changeDate;
				};
				const changes: ShiftChange[] = [];
				const acceptedRequests: Record<string, number[]> = {};
				const declinedRequests: Record<string, number[]> = {};

				for (const draftChange of draftChanges) {
					const { nurse, location, message, requestId, nurseToRemove, diff } = draftChange.payload;

					let changeType: ChangeType;
					const nurseData: NurseInShift | undefined = nurse ? { ...nurse } : undefined;

					switch (draftChange.type) {
						case DraftChangeType.ADD:
							changeType = ChangeType.Insert;
							if (nurseData) {
								nurseData.location = location;
								nurseData.isPending = false;
							}
							break;

						case DraftChangeType.REMOVE:
							changeType = ChangeType.Delete;
							if (nurseData) nurseData.message = message;
							break;

						case DraftChangeType.UPDATE:
							changeType = ChangeType.Update;
							if (nurseData && diff) Object.assign(nurseData, diff);
							break;

						case DraftChangeType.APPROVE_ADD_REQUEST:
							changeType = ChangeType.Insert;
							if (nurseData) {
								nurseData.isPending = false;
								nurseData.location = location;
							}
							if (requestId && nurseData) {
								acceptedRequests[requestId] ??= [];
								acceptedRequests[requestId].push(nurseData.nurseId);
							}
							break;

						case DraftChangeType.APPROVE_REPLACE_REQUEST:
							if (nurseToRemove) {
								changes.push({
									nurse: { ...nurseToRemove },
									...toChangeSlot(oldListSlotOf(dateOfChange(draftChange), nurseToRemove.shiftType)),
									shiftType: nurseToRemove.shiftType,
									date: dateOfChange(draftChange),
									departmentId: nurseToRemove.departmentId,
									changeType: ChangeType.Delete,
								});
							}
							changeType = ChangeType.Insert;

							if (nurseData) {
								nurseData.location = location;
								nurseData.isPending = false;
							}
							if (requestId && nurseData) {
								acceptedRequests[requestId] ??= [];
								acceptedRequests[requestId].push(nurseData.nurseId);
							}
							break;

						case DraftChangeType.UPDATE_CENSUS:
						case DraftChangeType.DELETE_CENSUS_OVERRIDE:
							continue;

						default:
							changeType = ChangeType.Update;
					}

					if (nurseData) {
						changes.push({
							nurse: nurseData,
							...toChangeSlot(oldListSlotOf(dateOfChange(draftChange), nurseData.shiftType)),
							shiftType: nurseData.shiftType,
							date: dateOfChange(draftChange),
							departmentId: nurseData.departmentId,
							changeType,
						});
					}
				}

				const updateRequestBody: PublishRequestBody = {
					accepted_requests: acceptedRequests,
					declined_requests: declinedRequests,
					actual_changes: [],
					changes,
					source: ChangesSource.DaySchedule,
				};

				setDraftModeActive(false);
				await Promise.all([
					publishChangesAsync(updateRequestBody),
					...censusUpdates.map((censusUpdate) =>
						backendService.createOrUpdateCensusOverride({ department_id: chosenDepartment.department_id, ...censusUpdate }),
					),
					...censusDeletes.map((censusDelete) => backendService.deleteCensusOverride({ department_id: chosenDepartment.department_id, ...censusDelete })),
				]);

				// A Draft Mode removal is only a local, unpersisted edit until this publish runs —
				// `useHandleNurseChange`'s own call-in invalidation skips itself in draft mode for exactly
				// that reason — so this is the one place a published draft removal needs it. Gated on the
				// batch actually containing a removal so an add/update-only publish doesn't refetch a cache
				// it never touched.
				if (changes.some((change) => change.changeType === ChangeType.Delete)) {
					void invalidateCallIns();
				}

				track("draft_changes_published", { change_count: draftChanges.length, approved_requests: locallyApprovedRequests.length });
				showSnackbar(t("draft.mode.published", { count: draftChanges.length + locallyApprovedRequests.length }), "success");

				await performGlobalRefreshCore({
					afterScheduleWrite,
					refreshNursesData,
					refreshRequests,
					fetchNotifications,
					refreshWageBreakdown,
					chosenDepartmentId: chosenDepartment.department_id,
					timezone: chosenDepartment.timezone,
					userId,
				});
				// The cached weeks still hold the drafts as local edits; reloading them shows what was saved.
				void resetScheduleAfterDraft(queryClient, chosenDepartment.department_id);

				disableDraftMode();
			} catch (error) {
				setDraftModeActive(true);
				showErrorSnackbar(t("draft.mode.publish.failed"), { change_count: draftChanges.length }, error);
				trackError("draft_changes_publish_failed", {
					change_count: draftChanges.length,
					approved_requests: locallyApprovedRequests.length,
					error: String(error),
				});
			}
		},
		[
			draftChanges,
			locallyApprovedRequests,
			originalDay,
			touchedDays,
			chosenDepartment,
			oldListSlotOf,
			disableDraftMode,
			afterScheduleWrite,
			refreshNursesData,
			refreshRequests,
			fetchNotifications,
			refreshWageBreakdown,
			showSnackbar,
			showErrorSnackbar,
			track,
			t,
			trackError,
			publishChangesAsync,
			invalidateCallIns,
			userId,
			queryClient,
		],
	);

	const publishChanges = useCallback(async (): Promise<void> => {
		if (!draftChanges.length && !locallyApprovedRequests.length) return showSnackbar(t("draft.mode.no.changes"), "warning");

		const censusUpdates: Array<{ date: string; shift_type: string; override_value: number; note?: string; source: ChangesSource }> = [];
		const censusDeletes: Array<{ date: string; shift_type: string; source: ChangesSource }> = [];

		for (const draftChange of draftChanges) {
			const { censusOverride, censusNote, date, shiftType, source } = draftChange.payload;

			if (draftChange.type === DraftChangeType.UPDATE_CENSUS) {
				if (date && shiftType && censusOverride !== undefined && source) {
					censusUpdates.push({ date, shift_type: shiftType, override_value: censusOverride, note: censusNote, source: source as ChangesSource });
				}
			} else if (draftChange.type === DraftChangeType.DELETE_CENSUS_OVERRIDE) {
				if (date && shiftType && source) {
					censusDeletes.push({ date, shift_type: shiftType, source: source as ChangesSource });
				}
			}
		}

		if (censusUpdates.length || censusDeletes.length) {
			const newCensus = censusUpdates.length > 0 ? censusUpdates[0].override_value : undefined;
			setPendingPublishData({ censusUpdates, censusDeletes, newCensus });
			setShowCensusWarning(true);
		} else {
			await executePublish(censusUpdates, censusDeletes);
		}
	}, [draftChanges, locallyApprovedRequests, showSnackbar, t, executePublish]);

	const handleCensusWarningConfirm = useCallback(async (): Promise<void> => {
		setShowCensusWarning(false);
		if (pendingPublishData) {
			await executePublish(pendingPublishData.censusUpdates, pendingPublishData.censusDeletes);
			setPendingPublishData(null);
		}
	}, [pendingPublishData, executePublish]);

	const handleCensusWarningCancel = useCallback((): void => {
		setShowCensusWarning(false);
		setPendingPublishData(null);
	}, []);

	const handleCancelNavigation = useCallback((): void => {
		if (blocker.state === "blocked") blocker.reset();
	}, [blocker]);

	const handleConfirmLeave = useCallback((): void => {
		if (blocker.state === "blocked") {
			discardChanges();
			blocker.proceed();
		}
	}, [blocker, discardChanges]);

	const handlePublishAndLeave = useCallback(async (): Promise<void> => {
		if (blocker.state === "blocked") {
			await publishChanges();
			blocker.proceed();
		}
	}, [blocker, publishChanges]);

	const isRequestLocallyApproved = useCallback((requestId: string): boolean => locallyApprovedRequests.includes(requestId), [locallyApprovedRequests]);

	return (
		<DraftModeContext.Provider
			value={{
				isDraftMode,
				draftChanges,
				hasUnsavedChanges,
				originalDay,
				enableDraftMode,
				disableDraftMode,
				publishChanges,
				discardChanges,
				isPublishing,
				locallyApprovedRequests,
				isRequestLocallyApproved,
				activeDayData,
			}}
		>
			{children}
			<NavigationWarningDialog
				open={blocker.state === "blocked"}
				onCancel={handleCancelNavigation}
				onConfirmLeave={handleConfirmLeave}
				onPublishAndLeave={handlePublishAndLeave}
				changeCount={draftChanges.length + locallyApprovedRequests.length}
				isPublishing={isPublishing}
			/>
			<CensusChangesWarningDialog
				open={showCensusWarning}
				onCancel={handleCensusWarningCancel}
				onConfirm={handleCensusWarningConfirm}
				newCensus={pendingPublishData?.newCensus}
			/>
		</DraftModeContext.Provider>
	);
};
